import { z } from 'zod'

export const MOCK_SCENARIOS = ['empty', 'populated', 'authenticated', 'customer', 'error'] as const

const RuntimeConfigSchema = z.object({
  API_BASE_URL: z.string().default('/api'),
  // Контур сотрудника (00-architecture § 3): origin employee-backend
  // (id.stit.tech) — продление (/auth/refresh) и выход сотрудника. В mock/dev
  // пусто → same-origin /auth/*, перехватывает MSW.
  ID_BASE_URL: z.string().default(''),
  // Контур заказчика (00-architecture § 3): origin company-backend — продление
  // (/auth/customer/refresh) и выход заказчика идут туда напрямую, мимо
  // календаря. Пусто = same-origin (MSW/dev). В проде — https://api.hub.stit.tech;
  // апекс календаря обязан быть в COMPANY_CORS_ORIGINS, иначе preflight режется
  // и продление вырождается в разлогин.
  HUB_API_BASE_URL: z.string().default(''),
  // Единая точка входа экосистемы (22-unified-login): страница хаба, куда уходят
  // обе аудитории без сессии. Своей формы входа у календаря нет — дефолт
  // указывает на прод-страницу, падать локально некуда.
  HUB_LOGIN_URL: z.string().default('https://hub.stit.tech/login'),
  // Зона организации (канон времени экосистемы): в ней считаются границы суток
  // и рисуется вся сетка. Зона устройства на календарь не влияет — иначе
  // «сегодня» у сотрудника в командировке разъезжается с бэком.
  APP_TZ: z.string().default('Europe/Moscow'),
  API_MODE: z.enum(['mock', 'real']).default('mock'),
  MOCK_SCENARIO: z.enum(MOCK_SCENARIOS).default('authenticated'),
  MOCK_DEVTOOLS: z.boolean().default(false),
  SENTRY_DSN: z.string().default(''),
  SENTRY_ENVIRONMENT: z.string().default('development'),
  RELEASE: z.string().default('dev'),
})

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

declare global {
  interface Window {
    __APP_CONFIG__?: unknown
  }
}

function readConfig(): RuntimeConfig {
  const env = import.meta.env as Record<string, string | undefined>
  const fromEnv = {
    APP_TZ: env.VITE_APP_TZ,
    ID_BASE_URL: env.VITE_ID_BASE_URL,
    HUB_API_BASE_URL: env.VITE_HUB_API_BASE_URL,
    HUB_LOGIN_URL: env.VITE_HUB_LOGIN_URL,
    API_MODE: env.VITE_API_MODE,
    MOCK_SCENARIO: env.VITE_MOCK_SCENARIO,
    MOCK_DEVTOOLS: env.VITE_MOCK_DEVTOOLS === '1',
  }
  const fromWindow =
    typeof window !== 'undefined' && window.__APP_CONFIG__
      ? (window.__APP_CONFIG__ as Record<string, unknown>)
      : {}
  return RuntimeConfigSchema.parse({ ...fromEnv, ...fromWindow })
}

export const runtimeConfig = readConfig()
